import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import {
  ArrowLeftIcon,
  UserIcon,
  CurrencyDollarIcon,
  TrophyIcon,
  CubeIcon,
  PencilIcon,
  PhotoIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { membersAPI } from '../services/api';
import { LoadingSpinner, StatCard, Badge, EmptyState, Modal } from '../components/common';
import { formatCurrency, formatDate, formatPhone } from '../utils/formatters';

// Base URL without /api/v1 for static files
const BASE_URL = process.env.REACT_APP_API_URL 
  ? process.env.REACT_APP_API_URL.replace(/\/api.*$/, '') 
  : 'http://localhost:3000';

export default function MemberDetailPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['member-profile', id],
    queryFn: () => membersAPI.getProfile(id),
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data) => membersAPI.updateProfile(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['member-profile', id]);
      toast.success('Profile updated successfully!');
      setIsEditModalOpen(false);
      reset();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    },
  });

  // Upload profile picture mutation
  const uploadPictureMutation = useMutation({
    mutationFn: (formData) => membersAPI.uploadProfilePicture(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries(['member-profile', id]);
      toast.success('Profile picture uploaded successfully!');
      setSelectedFile(null);
      setPreviewUrl(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to upload picture');
    },
  });

  // Remove profile picture mutation
  const removePictureMutation = useMutation({
    mutationFn: () => membersAPI.removeProfilePicture(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['member-profile', id]);
      toast.success('Profile picture removed successfully!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to remove picture');
    },
  });

  const onEditSubmit = (data) => {
    updateProfileMutation.mutate(data);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleUploadPicture = () => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append('profile_pic', selectedFile);
    uploadPictureMutation.mutate(formData);
  };

  const handleRemovePicture = () => {
    setIsRemoveModalOpen(true);
  };

  const confirmRemovePicture = () => {
    removePictureMutation.mutate();
    setIsRemoveModalOpen(false);
  };

  const openEditModal = () => {
    reset({
      name: memberData?.name || '',
      email: memberData?.email || '',
      address: memberData?.address || '',
      heir: memberData?.heir || '',
      heir_name: memberData?.heir_name || '',
      heir_phone: memberData?.heir_phone || '',
      heir_address: memberData?.heir_address || '',
    });
    setIsEditModalOpen(true);
  };
  // console.log(profile);

  const { data: deposits } = useQuery({
    queryKey: ['member-deposits', id],
    queryFn: () => membersAPI.getMemberDeposits(id),
    enabled: !!id,
  });
  console.log(deposits);

  if (isLoading) {
    return <LoadingSpinner text="Loading member details..." />;
  }

  const memberData = profile?.data?.data;
  const depositData = deposits?.data?.data;
  
  if (!memberData) {
    return (
      <EmptyState
        icon={UserIcon}
        title="Member not found"
        description="The member profile could not be loaded"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/members" className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
        </Link>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">
            {memberData?.name || formatPhone(memberData?.phone)}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Member ID: #{id} • {formatPhone(memberData?.phone)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={openEditModal}
            className="btn-primary flex items-center gap-2"
          >
            <PencilIcon className="h-4 w-4" />
            Edit Profile
          </button>
          <Badge variant={
            memberData?.role === 'admin' ? 'danger' :
            memberData?.role === 'staff' ? 'warning' :
            'info'
          }>
            {memberData?.role}
          </Badge>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Deposits"
          value={formatCurrency(memberData?.deposit_cents || 0)}
          icon={CurrencyDollarIcon}
          color="green"
        />
        <StatCard
          title="Days Covered"
          value={memberData?.days_covered || 0}
          icon={CubeIcon}
          color="blue"
        />
        <StatCard
          title="Payment Status"
          value={memberData?.payment_status?.toUpperCase() || 'N/A'}
          icon={CubeIcon}
          color={memberData?.payment_status === 'green' ? 'green' : memberData?.payment_status === 'yellow' ? 'yellow' : 'red'}
        />
        <StatCard
          title="Winner"
          value={memberData?.has_won ? 'Yes' : 'No'}
          icon={TrophyIcon}
          color={memberData?.has_won ? 'yellow' : 'gray'}
        />
      </div>

      {/* Profile Info */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Profile Information</h2>
          {memberData?.profile_pic && (
            <button
              onClick={handleRemovePicture}
              disabled={removePictureMutation.isPending}
              className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
            >
              <TrashIcon className="h-4 w-4" />
              Remove Picture
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Profile Picture */}
          <div className="md:col-span-2">
            <p className="text-sm text-gray-600 mb-2">Profile Picture</p>
            <div className="flex items-center gap-4">
              {memberData?.profile_pic || previewUrl ? (
                <img
                  src={previewUrl || `${BASE_URL}${memberData.profile_pic}`}
                  alt="Profile"
                  className="h-24 w-24 rounded-full object-cover border-2 border-gray-200"
                />
              ) : (
                <div className="h-24 w-24 rounded-full bg-gray-200 flex items-center justify-center">
                  <UserIcon className="h-12 w-12 text-gray-400" />
                </div>
              )}
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="profile-pic-upload"
                />
                <label
                  htmlFor="profile-pic-upload"
                  className="btn-secondary inline-flex items-center gap-2 cursor-pointer"
                >
                  <PhotoIcon className="h-4 w-4" />
                  Choose Picture
                </label>
                {selectedFile && (
                  <button
                    onClick={handleUploadPicture}
                    disabled={uploadPictureMutation.isPending}
                    className="btn-primary ml-2"
                  >
                    {uploadPictureMutation.isPending ? 'Uploading...' : 'Upload'}
                  </button>
                )}
              </div>
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-600">Name</p>
            <p className="font-medium text-gray-900">{memberData?.name || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Email</p>
            <p className="font-medium text-gray-900">{memberData?.email || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Phone</p>
            <p className="font-medium text-gray-900">{formatPhone(memberData?.phone)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Language</p>
            <p className="font-medium text-gray-900">{memberData?.language || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Address</p>
            <p className="font-medium text-gray-900">{memberData?.address || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Joined</p>
            <p className="font-medium text-gray-900">{formatDate(memberData?.user_created_at)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Heir Name</p>
            <p className="font-medium text-gray-900">{memberData?.heir_name || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Heir Phone</p>
            <p className="font-medium text-gray-900">{memberData?.heir_phone ? formatPhone(memberData.heir_phone) : '-'}</p>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          reset();
        }}
        title="Edit Member Profile"
      >
        <form onSubmit={handleSubmit(onEditSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              {...register('name', { required: 'Name is required' })}
              className="input-field"
              placeholder="Full name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              {...register('email')}
              className="input-field"
              placeholder="email@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <textarea
              {...register('address')}
              className="input-field"
              rows="2"
              placeholder="Full address"
            />
          </div>

          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Heir Information</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relationship
                </label>
                <select
                  {...register('heir')}
                  className="input-field"
                >
                  <option value="">Select relationship</option>
                  <option value="spouse">Spouse</option>
                  <option value="son">Son</option>
                  <option value="daughter">Daughter</option>
                  <option value="father">Father</option>
                  <option value="mother">Mother</option>
                  <option value="brother">Brother</option>
                  <option value="sister">Sister</option>
                  <option value="uncle">Uncle</option>
                  <option value="aunt">Aunt</option>
                  <option value="nephew">Nephew</option>
                  <option value="niece">Niece</option>
                  <option value="cousin">Cousin</option>
                  <option value="grandparent">Grandparent</option>
                  <option value="grandchild">Grandchild</option>
                  <option value="friend">Friend</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Heir Name
                </label>
                <input
                  type="text"
                  {...register('heir_name')}
                  className="input-field"
                  placeholder="Heir full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Heir Phone
                </label>
                <input
                  type="tel"
                  {...register('heir_phone')}
                  className="input-field"
                  placeholder="+251911234567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Heir Address
                </label>
                <textarea
                  {...register('heir_address')}
                  className="input-field"
                  rows="2"
                  placeholder="Heir address"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsEditModalOpen(false);
                reset();
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateProfileMutation.isPending}
              className="btn-primary"
            >
              {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Remove Picture Confirmation Modal */}
      <Modal
        isOpen={isRemoveModalOpen}
        onClose={() => setIsRemoveModalOpen(false)}
        title="Remove Profile Picture?"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-center">
            <div className="relative">
              {memberData?.profile_pic ? (
                <img
                  src={`${BASE_URL}${memberData.profile_pic}`}
                  alt="Profile"
                  className="h-32 w-32 rounded-full object-cover border-4 border-gray-200"
                />
              ) : (
                <div className="h-32 w-32 rounded-full bg-gray-200 flex items-center justify-center">
                  <UserIcon className="h-16 w-16 text-gray-400" />
                </div>
              )}
              <div className="absolute inset-0 rounded-full bg-red-500 bg-opacity-10 flex items-center justify-center">
                <TrashIcon className="h-12 w-12 text-red-500" />
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-gray-700 mb-2">
              This will permanently delete the profile picture.
            </p>
            <p className="text-sm text-gray-500">
              The member will see a default avatar instead.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => setIsRemoveModalOpen(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={confirmRemovePicture}
              disabled={removePictureMutation.isPending}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {removePictureMutation.isPending ? 'Removing...' : 'Yes, Remove Picture'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Current Batch Membership */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Current Batch Membership</h2>
        {memberData?.batch_id ? (
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-lg font-semibold text-gray-900">{memberData.batch_name || `Batch ${memberData.batch_number}`}</p>
                <p className="text-sm text-gray-600">Batch #{memberData.batch_number}</p>
              </div>
              <div className="flex gap-2">
                <Badge variant={
                  memberData.batch_status === 'active' ? 'success' :
                  memberData.batch_status === 'open' ? 'info' :
                  memberData.batch_status === 'completed' ? 'gray' :
                  'warning'
                }>
                  {memberData.batch_status}
                </Badge>
                <Badge variant={
                  memberData.payment_status === 'green' ? 'success' :
                  memberData.payment_status === 'yellow' ? 'warning' :
                  'danger'
                }>
                  {memberData.payment_status}
                </Badge>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Total Deposit</p>
                <p className="font-semibold text-gray-900">{formatCurrency(memberData.deposit_cents || 0)}</p>
              </div>
              <div>
                <p className="text-gray-600">Days Covered</p>
                <p className="font-semibold text-gray-900">{memberData.days_covered || 0} / {memberData.batch_total_days || 0}</p>
              </div>
              <div>
                <p className="text-gray-600">Pool Type</p>
                <p className="font-semibold text-gray-900">{memberData.is_fixed_pool ? 'Fixed' : 'Regular'}</p>
              </div>
              <div>
                <p className="text-gray-600">Enrolled</p>
                <p className="font-semibold text-gray-900">{formatDate(memberData.enrolled_at)}</p>
              </div>
              <div>
                <p className="text-gray-600">Last Payment</p>
                <p className="font-semibold text-gray-900">{memberData.last_payment_date ? formatDate(memberData.last_payment_date) : '-'}</p>
              </div>
              <div>
                <p className="text-gray-600">Next Due Date</p>
                <p className="font-semibold text-gray-900">{memberData.next_due_date ? formatDate(memberData.next_due_date) : '-'}</p>
              </div>
              <div>
                <p className="text-gray-600">Winner Status</p>
                <p className="font-semibold text-gray-900">{memberData.has_won ? '🏆 Won' : 'Not Won'}</p>
              </div>
              <div>
                <p className="text-gray-600">Payout Status</p>
                <p className="font-semibold text-gray-900">{memberData.payout_status?.replace('_', ' ') || '-'}</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-2">Batch Info</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div>
                  <p className="text-gray-500">Total Slots</p>
                  <p className="font-medium text-gray-900">{memberData.batch_total_slots?.toLocaleString() || 0}</p>
                </div>
                <div>
                  <p className="text-gray-500">Current Members</p>
                  <p className="font-medium text-gray-900">{memberData.batch_current_members || 0}</p>
                </div>
                <div>
                  <p className="text-gray-500">Total Days</p>
                  <p className="font-medium text-gray-900">{memberData.batch_total_days || 0}</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <EmptyState
            icon={CubeIcon}
            title="Not enrolled"
            description="This member is not currently enrolled in any batch"
          />
        )}
      </div>

      {/* Recent Deposits */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Deposits</h2>
        {depositData && depositData.length > 0 ? (
          <div className="space-y-3">
            {depositData.slice(0, 5).map((deposit) => (
              <div key={deposit.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{formatCurrency(deposit.amount_cents || 0)}</p>
                  <p className="text-sm text-gray-500">{formatDate(deposit.created_at)}</p>
                </div>
                <Badge variant={
                  deposit.status === 'completed' ? 'success' :
                  deposit.status === 'pending' ? 'warning' :
                  'danger'
                }>
                  {deposit.status}
                </Badge>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={CurrencyDollarIcon}
            title="No deposits yet"
            description="Deposit history will appear here"
          />
        )}
      </div>
    </div>
  );
}
